import React, {useEffect, useState, useContext, useMemo} from 'react';
import {Modal, Button, Badge} from 'react-bootstrap';
import {IconUserCheck, IconX} from '@tabler/icons-react';
import {useParams} from 'react-router-dom';
import {ScreenDataTable} from './ScreenDataTable';
import {FormModal} from './FormModal';
import {UpdateFormModal} from './UpdateFormModal';
import {AppContext} from '../contexts/AppContext';
import {useQueryClient} from '@tanstack/react-query';
import {getRowDisplayValue, getRowLabel} from '../utils/screenDisplayValue';
import {filtersOwnedByForm} from '../utils/pickerFilters';

interface SelectModalProps {
    show: boolean;
    onHide: () => void;
    screenId: string;
    onSelect: (row: any, displayValue: string) => void;
    onOpenCreateForm?: (form: any) => void;
    screenData?: any;
    /** Collect several rows and confirm once, instead of closing on first click. */
    multiple?: boolean;
    onSelectMany?: (added: any[], removed: any[]) => void;
    confirmLabel?: string;
    /** Rows already attached to the parent - shown ticked from the start. */
    assignedRows?: any[];
    /** Whether unticking an attached row is offered (the link can be cleared). */
    allowRemove?: boolean;
    /**
     * Current values of the form the picker was opened from. A context filter
     * of the picker screen takes the value of the form field with the same
     * code, or of the field whose screen column carries that code as its
     * contextCode - so the list narrows by what is filled in right now, and a
     * value the user has changed but not saved wins over the stored context.
     */
    formValues?: Record<string, any>;
    /** The screen the form belongs to; its columns map field codes to context codes. */
    formScreen?: any;
    /** Only picking: the screen's update forms and delete are not offered (statistic filters). */
    pickOnly?: boolean;
    /** Offers "Aktuálny používateľ" above the table; picking it stands for the signed-in user. */
    onPickCurrentUser?: () => void;
}

export const SelectModal: React.FC<SelectModalProps> = ({
                                                            show,
                                                            onHide,
                                                            screenId,
                                                            onSelect,
                                                            screenData,
                                                            multiple = false,
                                                            onSelectMany,
                                                            confirmLabel = 'Priradiť',
                                                            assignedRows,
                                                            allowRemove = false,
                                                            formValues,
                                                            formScreen,
                                                            pickOnly = false,
                                                            onPickCurrentUser
                                                        }) => {
    const {applications} = useContext(AppContext);
    const {appId: routeAppId} = useParams<{appId: string}>();
    const queryClient = useQueryClient();
    const [screen, setScreen] = useState<any>(null);
    const [originalScreen, setOriginalScreen] = useState<any>(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [selectedForm, setSelectedForm] = useState<any>(null);
    // A row being edited from the picker: the screen's update form opens in a second modal.
    const [updateTarget, setUpdateTarget] = useState<{form: any; row: any} | null>(null);

    useEffect(() => {
        if (show && screenId) {
            let foundScreen = screenData || null;

            if (!foundScreen) {
                for (const app of applications) {
                    if (app.screens) {
                        foundScreen = app.screens.find((s: any) => String(s.id) === String(screenId));
                        if (foundScreen) break;
                    }
                }
            }

            if (foundScreen) {
                setOriginalScreen(foundScreen);
                // The picker keeps the screen's own update forms and delete
                // switch: a lookup row can be corrected or removed right here.
                setScreen({
                    ...foundScreen,
                    ...(pickOnly ? {updateForms: [], deleteEnabled: false} : {}),
                    columns: foundScreen.columns.map((col: any) => ({
                        ...col,
                        filterable: false
                    }))
                });
            }
        }
    }, [show, screenId, applications, screenData, pickOnly]);

    const handleFormSuccess = (newRecord?: any) => {
        setShowFormModal(false);
        setSelectedForm(null);

        if (newRecord && screen) {
            queryClient.refetchQueries({queryKey: ["screenData", screen.id]});
        }


    };

    // The modal is a checklist over the parent's children: rows already attached
    // start ticked, newly ticked rows get attached, unticked ones get removed.
    const [picked, setPicked] = useState<any[]>([]);
    const [removed, setRemoved] = useState<any[]>([]);

    // Reopening the modal must not inherit the previous run's edits.
    useEffect(() => {
        if (!show) {
            setPicked([]);
            setRemoved([]);
        }
    }, [show]);

    const assigned = assignedRows || [];
    const sameRow = (a: any, b: any) => String(a?.id) === String(b?.id);
    const isAssigned = (row: any) => assigned.some(a => sameRow(a, row));
    const isRemoved = (row: any) => removed.some(r => sameRow(r, row));
    const isPicked = (row: any) => picked.some(p => sameRow(p, row));

    const checkedRows = [...assigned.filter(a => !isRemoved(a)), ...picked];

    const toggleRow = (row: any) => {
        if (isPicked(row)) {
            setPicked(prev => prev.filter(p => !sameRow(p, row)));
            return;
        }

        if (isAssigned(row)) {
            // Unticking clears the child's link to the parent; where that key
            // cannot be nulled the row simply stays attached.
            if (!allowRemove) return;
            setRemoved(prev => isRemoved(row) ? prev.filter(r => !sameRow(r, row)) : [...prev, row]);
            return;
        }

        setPicked(prev => [...prev, row]);
    };

    const handleRowClick = (row: any) => {
        if (!multiple) {
            onSelect(row, getRowDisplayValue(row, screen?.columns));
            onHide();
            return;
        }

        toggleRow(row);
    };

    const handleConfirm = () => {
        if (picked.length === 0 && removed.length === 0) return;
        onSelectMany?.(picked, removed);
        setPicked([]);
        setRemoved([]);
    };

    const handleCreateClick = (form: any) => {
        setSelectedForm(form);
        setShowFormModal(true);
    };

    const handleUpdateClick = (form: any, row: any) => {
        setUpdateTarget({form, row});
    };

    const handleUpdateSuccess = () => {
        setUpdateTarget(null);
        if (screen) {
            queryClient.refetchQueries({queryKey: ["screenData", screen.id]});
        }
    };

    const appId = routeAppId || originalScreen?.id;
    const allScreens = applications.flatMap((app: any) => app.screens || []);
    const ownedFilters = filtersOwnedByForm(originalScreen, formScreen, formValues);
    const ownedKey = JSON.stringify(ownedFilters);
    // Filled form values become explicit filters; an empty form value removes
    // the filter from the table's view so stale stored context is not sent.
    const {tableScreen, formContextFilters} = useMemo(() => {
        const owned: Record<string, string | null> = JSON.parse(ownedKey);
        const explicit: Record<string, string> = {};
        Object.entries(owned).forEach(([code, value]) => { if (value !== null) explicit[code] = value; });
        const emptyCodes = Object.keys(owned).filter(code => owned[code] === null);
        const withoutEmpty = screen && emptyCodes.length > 0
            ? {
                ...screen,
                filters: (screen.filters ?? []).filter((f: any) => !emptyCodes.includes(f.code)),
                queryParameters: (screen.queryParameters ?? []).filter((code: string) => !emptyCodes.includes(code)),
            }
            : screen;
        return {tableScreen: withoutEmpty, formContextFilters: explicit};
    }, [screen, ownedKey]);

    if (!screen) {
        return (
            <Modal show={show} onHide={onHide} size="xl" className="modal-blur" backdrop="static" data-e2e={`selectmodal:${screenId}`}>
                <Modal.Header closeButton>
                    <Modal.Title>Načítavam...</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center p-4">Načítavam...</div>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <>
            <Modal show={show} onHide={onHide} size="xl" className="modal-blur" backdrop="static" data-e2e={`selectmodal:${screenId}`}>
                <Modal.Header closeButton>
                    <Modal.Title>{screen.displayName || screen.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {onPickCurrentUser && (
                        <div className="mb-3">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-inline-flex align-items-center gap-1"
                                onClick={() => { onPickCurrentUser(); onHide(); }}
                                data-e2e="selectmodal:current-user"
                            >
                                <IconUserCheck size={16}/> Aktuálny používateľ
                            </Button>
                        </div>
                    )}
                    <ScreenDataTable
                        screen={tableScreen}
                        selectionMode={true}
                        embedded={true}
                        filters={Object.keys(formContextFilters).length > 0 ? formContextFilters : undefined}
                        onRowClick={handleRowClick}
                        onOpenCreateForm={handleCreateClick}
                        onOpenUpdateForm={pickOnly ? undefined : handleUpdateClick}
                        selectedRowIds={multiple ? checkedRows.map(r => r.id) : undefined}
                        lockedRowIds={multiple && !allowRemove ? assigned.map(a => a.id) : undefined}
                    />
                </Modal.Body>
                {multiple && (
                    <Modal.Footer className="d-flex justify-content-between align-items-center gap-3">
                        <div className="select-modal-picks d-flex gap-1 flex-wrap align-items-center">
                            {checkedRows.length === 0 ? (
                                <span className="text-secondary">Kliknutím na riadok vyberiete záznamy.</span>
                            ) : checkedRows.map(r => {
                                const removable = !isAssigned(r) || allowRemove;
                                return (
                                    <Badge
                                        key={r.id}
                                        bg={removable ? 'primary' : 'secondary'}
                                        className="d-inline-flex align-items-center"
                                        title={removable ? undefined : 'Už priradené — väzbu na tomto stĺpci nie je možné zrušiť'}
                                    >
                                        {getRowLabel(r, screen?.columns)}
                                        {removable && (
                                            <button
                                                type="button"
                                                onClick={() => toggleRow(r)}
                                                className="p-0 ms-1 border-0 bg-transparent"
                                                style={{lineHeight: 1, color: 'inherit', cursor: 'pointer'}}
                                                aria-label="Odobrať z výberu"
                                            >
                                                <IconX size={12}/>
                                            </button>
                                        )}
                                    </Badge>
                                );
                            })}
                        </div>
                        <div className="d-flex align-items-center gap-3 flex-shrink-0">
                            {removed.length > 0 && (
                                <span className="text-warning">Odoberie sa: {removed.length}</span>
                            )}
                            <Button
                                variant="primary"
                                disabled={picked.length === 0 && removed.length === 0}
                                onClick={handleConfirm}
                            >
                                {confirmLabel}{picked.length > 0 ? ` (${picked.length})` : ''}
                            </Button>
                        </div>
                    </Modal.Footer>
                )}
            </Modal>

            {selectedForm && (
                <FormModal
                    show={showFormModal}
                    onHide={() => {
                        setShowFormModal(false);
                        setSelectedForm(null);
                    }}
                    form={selectedForm}
                    appId={appId}
                    onSuccess={(data) => handleFormSuccess(data)}
                />
            )}

            {updateTarget && (
                <UpdateFormModal
                    show={true}
                    onHide={() => setUpdateTarget(null)}
                    form={updateTarget.form}
                    row={updateTarget.row}
                    appId={appId}
                    allScreens={allScreens}
                    onSuccess={handleUpdateSuccess}
                />
            )}
        </>
    );
};
